//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';

import { facebookInstagramSettingsActions } from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsActions';
import { getPageById } from '@apis/gateway-instagram/facebookGraphApiSearchApi';
import {
  getClientIsOnboarded as fetchClientIsOnboarded,
  getPageMetadata as fetchPageMetadata,
  setOnboarded,
} from '@apis/gatewayFacebookApi';
import {
  IFacebookPageSuggestion,
  MentionsSuggestionType,
} from '@components/molecules/smart-input/smartInputModel';
import {
  FACEBOOK_PAGE_ACTIONS,
  VERIFIED_PAGE_STATUS,
} from '@facebook/constants/facebookConstants';
import { IFacebookPageMetadata } from '@facebook/models/facebookPostDetailsModel';
import FacebookPageId from '@models/FacebookPageId';
import { IFacebookPage } from '@models/IFacebookPage';
import { createThunk } from '@redux/createThunk';
import { createAction } from '@utils/actions';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { FetchActionCreators, makeFetchActions } from '@utils/makeFetchActions';

import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';

function buildFacebookPageSuggestion(
  page: IFacebookPage,
): IFacebookPageSuggestion {
  const facebookUserSuggestion: IFacebookPageSuggestion = {
    id: FacebookPageId(page.id),
    isVerified: page.verificationStatus === VERIFIED_PAGE_STATUS,
    link: page.link ?? '',
    name: page.name ?? '',
    type: MentionsSuggestionType.FacebookPage,
  };
  return facebookUserSuggestion;
}

function requestPageMetadata() {
  return createAction({
    type: FACEBOOK_PAGE_ACTIONS.REQUEST_PAGE_METADATA,
  });
}

function receivePageMetadata(pageMetadata: IFacebookPageMetadata) {
  return createAction({
    type: FACEBOOK_PAGE_ACTIONS.RECEIVE_PAGE_METADATA,
    pageMetadata,
  });
}

function setPagesById(page: PojoMap<FacebookPageId, IFacebookPageSuggestion>) {
  return createAction({
    type: FACEBOOK_PAGE_ACTIONS.RECEIVE_TAGGED_PAGE,
    page,
  });
}

function receiveGetPageByIdFailure(
  pageId: FacebookPageId,
  failureMessage: string,
) {
  return createAction({
    type: FACEBOOK_PAGE_ACTIONS.RECIEVE_GET_PAGE_BY_ID_FAILED,
    pageId,
    failureMessage,
  });
}

const getFacebookAndInstagramConnectionInfo = makeFetchActions(
  FACEBOOK_PAGE_ACTIONS.REQUEST_FB_IG_CONNECTION_INFO,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_FB_IG_CONNECTION_INFO_SUCCEEDED,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_FB_IG_CONNECTION_INFO_FAILED,
);

const getFacebookPageConnectionInfo = makeFetchActions(
  FACEBOOK_PAGE_ACTIONS.REQUEST_FACEBOOK_PAGE_CONNECTION_INFO,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_FACEBOOK_PAGE_CONNECTION_INFO_SUCCEEDED,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_FACEBOOK_PAGE_CONNECTION_INFO_FAILED,
  (isClientOnboarded: boolean) => ({ isClientOnboarded }),
);

const setIsClientOnboarded = makeFetchActions(
  FACEBOOK_PAGE_ACTIONS.REQUEST_SET_IS_ONBOARDED,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_SET_IS_ONBOARDED_SUCCEEDED,
  FACEBOOK_PAGE_ACTIONS.RECEIVE_SET_IS_ONBOARDED_FAILED,
);

export type FacebookPageActions = ReturnType<
  | typeof requestPageMetadata
  | typeof receivePageMetadata
  | typeof setPagesById
  | typeof receiveGetPageByIdFailure
  | FetchActionCreators<typeof getFacebookAndInstagramConnectionInfo>
  | FetchActionCreators<typeof getFacebookPageConnectionInfo>
  | FetchActionCreators<typeof setIsClientOnboarded>
>;

const getPageMetadata =
  (pageId: FacebookPageId) => async (dispatch: ReduxDispatch) => {
    const response = await fetchPageMetadata(pageId);

    dispatch(receivePageMetadata(response));
  };

export const facebookPageActions = {
  getPageMetadata,
  fetchPageTokenAndMetadata:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const { connectedFacebookPageToken } =
        getState().facebookInstagramSettings;

      if (isUndefined(connectedFacebookPageToken)) {
        await dispatch(
          facebookInstagramSettingsActions.fetchConnectedFacebookPage(),
        );
      }

      const facebookPageId =
        getState().facebookInstagramSettings.connectedFacebookPageToken
          ?.entityId;

      if (isDefined(facebookPageId)) {
        await dispatch(getPageMetadata(facebookPageId));
      }
    },
  fetchFacebookAndInstagramConnectionInfo:
    () => async (dispatch: ReduxDispatch) => {
      dispatch(getFacebookAndInstagramConnectionInfo.requested());

      try {
        await Promise.all([
          dispatch(
            facebookInstagramSettingsActions.fetchConnectedFacebookPage(),
          ),
          dispatch(facebookInstagramSettingsActions.fetchFacebookUserToken()),
          dispatch(
            facebookInstagramSettingsActions.fetchConnectedInstagramAccount(),
          ),
        ]);
        dispatch(getFacebookAndInstagramConnectionInfo.succeeded());
      } catch (_e) {
        dispatch(
          getFacebookAndInstagramConnectionInfo.failed(
            'An error occurred while fetching Facebook and Instagram connection info. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
  fetchFacebookPageConnectionInfo: () => async (dispatch: ReduxDispatch) => {
    dispatch(getFacebookPageConnectionInfo.requested());
    try {
      const [isClientOnboarded] = await Promise.all([
        fetchClientIsOnboarded(),
        dispatch(facebookPageActions.fetchFacebookAndInstagramConnectionInfo()),
      ]);
      dispatch(getFacebookPageConnectionInfo.succeeded(isClientOnboarded));
    } catch (_e) {
      dispatch(
        getFacebookPageConnectionInfo.failed(
          'An error occurred while fetching Facebook page connection info. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
  setIsClientOnboarded: () => async (dispatch: ReduxDispatch) => {
    dispatch(setIsClientOnboarded.requested());
    try {
      await setOnboarded();
      dispatch(setIsClientOnboarded.succeeded());
    } catch (_e) {
      dispatch(
        setIsClientOnboarded.failed(
          'An error occurred while setting the client as onboarded. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
  getFacebookPageById: createThunk(
    (pageId: FacebookPageId) => async (dispatch, getState) => {
      const taggedPages = getState().facebook.pageDetails.taggedPages.data;
      const invalidPages =
        getState().facebook.pageDetails.taggedPages.invalidPages;

      // Check if there is already a page suggestion or failed request for this pageId
      // (helps prevent hitting rate limit)
      const facebookPage = PojoMap.get(taggedPages, pageId);
      const invalidPage = PojoMap.get(invalidPages, pageId);

      if (facebookPage || invalidPage) {
        // If the facebookPage is not defined then there is a failed request associated
        // with this pageId and we still want to return undefined here.
        return facebookPage;
      }

      try {
        const response = await getPageById(pageId);
        const pageSuggestionResult = buildFacebookPageSuggestion(response);

        dispatch(
          setPagesById({
            [pageId]: pageSuggestionResult,
          }),
        );

        return pageSuggestionResult;
      } catch (e) {
        // Save the error message by pageId. This will prevent additional requests for ids
        // that produced a failed response already
        dispatch(
          receiveGetPageByIdFailure(
            pageId,
            'An error occurred while fetching the Facebook page. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },
  ),
};
